import { NavLink } from "react-router-dom";
import { Dropdown, Tooltip } from "antd";
import {
  ApartmentOutlined,
  BarChartOutlined,
  CommentOutlined,
  CompassOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  GlobalOutlined,
  InstagramOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  OrderedListOutlined,
  PlayCircleOutlined,
  ReadOutlined,
  ReloadOutlined,
  RocketOutlined,
  ScanOutlined,
  SettingOutlined,
  ShopOutlined,
  ThunderboltOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../contexts/AuthContext";
import { PAGE_IDS, PAGE_TITLES } from "../../utils/constants";
import { cn } from "../../utils/cn";
import styles from "./Sidebar.module.css";

const MapIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 7l5-2 5 2 5-2v12l-5 2-5-2-5 2V7z" />
    <path d="M8 5v14" />
    <path d="M13 7v12" />
  </svg>
);

/**
 * The sidebar, in the order the work actually happens.
 *
 * It had grown by appending: Queue Monitor sat below Outlet Profile, the
 * collection lanes were scattered through the list, and a retired page sat
 * fourth from the top. Four pairs also shared an icon, so the collapsed
 * rail carried four ambiguous glyphs.
 *
 * The order now follows a run: the status board, the map a run starts from,
 * the finder jobs it produces, the queue working them. Then the collection
 * lanes in the SAME order the Delivery Readiness board lists them, then what
 * the data becomes, then admin, then what is retired.
 */
export const SECTIONS = [
  {
    title: null,
    items: [
      // Not ReloadOutlined: that is Respider's, and two adjacent rows with
      // the same circular arrow is how this page got mistaken for it.
      { id: PAGE_IDS.PIPELINE_STATUS, path: "/pipeline-status", icon: DashboardOutlined },
      // Ahead of the job list it produces: this is where a run starts.
      { id: PAGE_IDS.MAP, path: "/map", icon: GlobalOutlined },
      { id: PAGE_IDS.JOBS, path: "/jobs", icon: CompassOutlined },
      // Fleet Monitor (was Queue Monitor): every task in the cluster, then the
      // queues. Not DatabaseOutlined: Lakehouse has it.
      { id: PAGE_IDS.QUEUE_MONITOR, path: "/queue-monitor", icon: BarChartOutlined },
    ],
  },
  {
    // Detail fresh, Instagram, Reviews fresh, Menu OCR / Vibe detect -- the
    // Delivery Readiness columns, left to right.
    title: "Collection",
    items: [
      { id: PAGE_IDS.RESPIDER, path: "/respider", icon: ReloadOutlined },
      { id: PAGE_IDS.INSTAGRAM, path: "/instagram", icon: InstagramOutlined },
      { id: PAGE_IDS.GMAPS_REVIEWS, path: "/gmaps-reviews", icon: CommentOutlined },
      { id: PAGE_IDS.MENU_OCR, path: "/menu-ocr", icon: ScanOutlined },
      { id: PAGE_IDS.SERPER_ENRICHER, path: "/serper-enricher", icon: ThunderboltOutlined },
      { id: PAGE_IDS.NEWS_SIGNALS, path: "/news-signals", icon: ReadOutlined },
    ],
  },
  {
    title: "Data",
    items: [
      { id: PAGE_IDS.ENTITY_RESOLUTION, path: "/entity-resolution", icon: ApartmentOutlined },
      { id: PAGE_IDS.DATA_PLATFORM, path: "/data-platform", icon: DatabaseOutlined },
      { id: PAGE_IDS.OUTLETS, path: "/outlets", icon: ShopOutlined },
      { id: PAGE_IDS.ETL_PIPELINE, path: "/etl", icon: RocketOutlined },
    ],
  },
  {
    title: "Admin",
    items: [
      // Not ThunderboltOutlined: Serper Enricher has it.
      { id: PAGE_IDS.BATCH_JOB_RUNNER, path: "/batch/new", icon: PlayCircleOutlined },
      { id: PAGE_IDS.BATCH_JOBS, path: "/batch/jobs", icon: OrderedListOutlined },
      { id: PAGE_IDS.API_KEYS, path: "/api-keys", icon: KeyOutlined },
      { id: "config", path: "/config", label: "Config", icon: SettingOutlined },
    ],
  },
  {
    // Named, not just moved to the bottom. A row that is merely last still
    // looks live; one under this heading does not, and it stays reachable
    // for whoever needs the history.
    title: "Retired",
    dim: true,
    items: [{ id: PAGE_IDS.SOCIAL_YTTT, path: "/social-yttt", icon: YoutubeOutlined }],
  },
];

function NavItem({ item, collapsed, dim, onNavigate }) {
  const label = item.label ?? PAGE_TITLES[item.id];
  const Icon = item.icon;
  // No tooltip: the folded rail peeks open 150 ms after the pointer arrives,
  // labels and all, so a tooltip could only blink before it.
  return (
    // NavLink matches by path segment: /jobs is current on /jobs/abc, and
    // /batch/new is not current on /batch/jobs.
    <NavLink
      to={item.path}
      aria-label={collapsed ? label : undefined}
      onClick={onNavigate}
      className={({ isActive }) => cn(styles.item, isActive && styles.active, dim && styles.dim)}
    >
      <Icon className={styles.icon} aria-hidden="true" />
      {!collapsed && <span className={styles.label}>{label}</span>}
    </NavLink>
  );
}

export default function Sidebar({ collapsed, onToggle, floating = false, onNavigate }) {
  const { user, signOut } = useAuth();
  const displayName = user?.displayName || (user?.email ? user.email.split("@")[0] : "User");
  const initial = displayName.charAt(0).toUpperCase() || "U";
  // Floating (peeking), the button pins the sidebar open.
  const toggleLabel = floating ? "Keep sidebar open" : collapsed ? "Expand sidebar" : "Collapse sidebar";

  return (
    <aside
      aria-label="Sidebar"
      data-collapsed={String(collapsed)}
      data-floating={String(floating)}
      className={cn(styles.sidebar, collapsed && styles.collapsed, floating && styles.floating)}
    >
      <div className={styles.brand}>
        <MapIcon className={styles.brandIcon} />
        {!collapsed && <span>Map Collector</span>}
      </div>

      <nav aria-label="Main navigation" className={styles.nav}>
        {SECTIONS.map((section, index) => (
          <div key={section.title ?? "top"}>
            {/* One slot, folded or open: a peek must not move any row under the pointer. */}
            {index > 0 && (
              <div className={styles.section} role={collapsed ? "separator" : undefined}>
                {!collapsed && section.title}
              </div>
            )}
            {section.items.map((item) => (
              <NavItem key={item.path} item={item} collapsed={collapsed} dim={section.dim} onNavigate={onNavigate} />
            ))}
          </div>
        ))}
      </nav>

      <div className={styles.footer}>
        {user && (
          <Dropdown
            trigger={["click"]}
            placement="topLeft"
            overlayStyle={{ zIndex: 2100 }}
            menu={{
              items: [
                { key: "email", label: user.email, disabled: true },
                { type: "divider" },
                {
                  key: "signout",
                  label: "Sign out",
                  icon: <LogoutOutlined />,
                  danger: true,
                  onClick: () => signOut(),
                },
              ],
            }}
          >
            <button type="button" className={styles.user} aria-label={`Account: ${displayName}`}>
              <span className={styles.avatar} aria-hidden="true">
                {initial}
              </span>
              {!collapsed && <span className={styles.name}>{displayName}</span>}
            </button>
          </Dropdown>
        )}
        <Tooltip title={`${toggleLabel}  [`} placement="right" zIndex={2100}>
          <button
            type="button"
            className={styles.toggle}
            onClick={onToggle}
            aria-label={toggleLabel}
            aria-expanded={!collapsed && !floating}
          >
            {collapsed || floating ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
        </Tooltip>
      </div>
    </aside>
  );
}
